import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/FirebaseAuthContext';
import { ref, get } from 'firebase/database';
import { db } from '../config/firebase';
import TutorialVideo from './TutorialVideo';
import { calculateMoCAScore } from '../utils/mocaScoring';
import MoCAAssessment from './MoCAAssessment';
import './AdminDashboard.css';

interface PatientData {
  uid: string;
  email?: string;
  displayName: string;
  role: string;
  createdAt: string;
  lastLogin: string;
  isAnonymous: boolean;
}

interface SessionData {
  sessionId: string;
  userId: string;
  gameType: string;
  startTime: string;
  endTime: string | null;
  isActive: boolean;
  createdAt: string;
  resultsExpanded?: boolean;
  results?: any[];
}

interface PatientWithSessions extends PatientData {
  sessions: SessionData[];
  expanded: boolean;
}

export const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [patients, setPatients] = useState<PatientWithSessions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameType, setSelectedGameType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'sessions'>('date');
  const [showTutorial, setShowTutorial] = useState(false);
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedPatientForMoCA, setSelectedPatientForMoCA] = useState<PatientWithSessions | null>(null);

  // Inline Admin Service functions
  const getAllPatients = async (): Promise<PatientData[]> => {
    const usersRef = ref(db, 'users');
    const snapshot = await get(usersRef);
    
    if (snapshot.exists()) {
      const users = snapshot.val();
      return Object.keys(users).map(uid => ({
        uid,
        ...users[uid]
      })).filter(user => user.role !== 'admin');
    }
    
    return [];
  };

  const getPatientSessions = async (userId: string): Promise<SessionData[]> => {
    const sessionsRef = ref(db, 'gameSessions');
    const snapshot = await get(sessionsRef);
    
    if (snapshot.exists()) {
      const sessions = snapshot.val();
      return Object.keys(sessions)
        .map(sessionId => ({
          sessionId,
          ...sessions[sessionId],
          resultsExpanded: false,
          results: []
        }))
        .filter(session => session.userId === userId)
        .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
    }
    
    return [];
  };

  const getSessionResults = async (sessionId: string, gameType: string): Promise<any[]> => {
    let resultsPath = '';
    
    switch (gameType) {
      case 'NumberSequence':
        resultsPath = 'numberSequenceResults';
        break;
      case 'MemoryPalace':
        resultsPath = 'memoryPalaceResults';
        break;
      case 'ToneMemory':
        resultsPath = 'toneMemoryResults';
        break;
      case 'ImageMatching':
        resultsPath = 'imageMatchingResults';
        break;
      case 'TickerTape':
        resultsPath = 'tickerTapeResults';
        break;
      default:
        return [];
    }
    
    const resultsRef = ref(db, resultsPath);
    const snapshot = await get(resultsRef);
    
    if (snapshot.exists()) {
      const results = snapshot.val();
      return Object.keys(results)
        .map(resultId => ({
          resultId,
          ...results[resultId]
        }))
        .filter(result => result.sessionId === sessionId)
        .sort((a, b) => new Date(a.timestamp || a.savedAt).getTime() - new Date(b.timestamp || b.savedAt).getTime());
    }
    
    return [];
  };

  useEffect(() => {
    loadPatientsData();
  }, []);

  const loadPatientsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const allPatients = await getAllPatients();
      
      // Load sessions for each patient
      const patientsWithSessions = await Promise.all(
        allPatients.map(async (patient: PatientData) => {
          const sessions = await getPatientSessions(patient.uid);
          return {
            ...patient,
            sessions,
            expanded: false
          };
        })
      );
      
      setPatients(patientsWithSessions);
    } catch (err) {
      console.error('Error loading patients data:', err);
      setError('Failed to load patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePatientExpanded = (patientUid: string) => {
    setPatients(prev =>
      prev.map(p =>
        p.uid === patientUid ? { ...p, expanded: !p.expanded } : p
      )
    );
  };

  const toggleSessionResults = async (patientUid: string, sessionId: string, gameType: string) => {
    const patient = patients.find(p => p.uid === patientUid);
    const session = patient?.sessions.find(s => s.sessionId === sessionId);
    
    if (!session) return;
    
    if (!session.resultsExpanded) {
      // Load results if not already loaded
      if (!session.results || session.results.length === 0) {
        const results = await getSessionResults(sessionId, gameType);
        setPatients(prev =>
          prev.map(p =>
            p.uid === patientUid
              ? {
                  ...p,
                  sessions: p.sessions.map(s =>
                    s.sessionId === sessionId
                      ? { ...s, results, resultsExpanded: true }
                      : s
                  )
                }
              : p
          )
        );
      } else {
        // Just toggle expansion
        setPatients(prev =>
          prev.map(p =>
            p.uid === patientUid
              ? {
                  ...p,
                  sessions: p.sessions.map(s =>
                    s.sessionId === sessionId
                      ? { ...s, resultsExpanded: true }
                      : s
                  )
                }
              : p
          )
        );
      }
    } else {
      // Collapse results
      setPatients(prev =>
        prev.map(p =>
          p.uid === patientUid
            ? {
                ...p,
                sessions: p.sessions.map(s =>
                  s.sessionId === sessionId
                    ? { ...s, resultsExpanded: false }
                    : s
                )
              }
            : p
        )
      );
    }
  };

  const getGameTypeLabel = (gameType: string): string => {
    const labels: { [key: string]: string } = {
      'NumberSequence': 'Number Sequencing',
      'MemoryPalace': 'Memory Palace',
      'ToneMemory': 'Tone Memory',
      'ImageMatching': 'Image Matching',
      'TickerTape': 'Ticker Tape Rush'
    };
    return labels[gameType] || gameType;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start: string, end: string | null): string => {
    if (!end) return 'Active';
    const duration = new Date(end).getTime() - new Date(start).getTime();
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  // Export ALL data from database (unfiltered)
  const exportAllData = async () => {
    // Prepare data for export
    const exportData: any[] = [];
    
    // Use original patients data (not filtered)
    patients.forEach(patient => {
      patient.sessions.forEach(session => {
        if (session.results && session.results.length > 0) {
          session.results.forEach((result: any, index: number) => {
            const row: any = {
              // Patient Info
              'Patient Name': patient.displayName,
              'Patient Email': patient.email || 'N/A',
              'Patient ID': patient.uid,
              'Patient Type': patient.isAnonymous ? 'Anonymous' : 'Registered',
              
              // Session Info
              'Session ID': session.sessionId,
              'Game Type': getGameTypeLabel(session.gameType),
              'Session Start': formatDate(session.startTime),
              'Session End': session.endTime ? formatDate(session.endTime) : 'Active',
              'Session Duration': formatDuration(session.startTime, session.endTime),
              
              // Result Info
              'Round/Level': result.level || result.round || result.roundNumber || index + 1,
              'Timestamp': formatDate(result.timestamp || result.savedAt),
              'Accuracy %': Math.round(result.accuracyPercentage || result.accuracy || 0),
              'Score': result.score || 0,
            };

            // Add time data
            let time = result.completionTimeSeconds || result.timeSeconds || result.reactionTimeSeconds || 
                       result.time || result.duration || 0;
            if (!time && result.timestampEnd && result.timestampStart) {
              time = (new Date(result.timestampEnd).getTime() - new Date(result.timestampStart).getTime()) / 1000;
            }
            row['Time (seconds)'] = time > 0 ? time : 'N/A';

            // Add game-specific details
            if (session.gameType === 'NumberSequence') {
              row['Grid Size'] = `${result.gridSize}x${result.gridSize}`;
              row['Obstacles'] = result.obstaclesCount || 0;
              row['Moves'] = result.movesCount || 0;
              row['Errors'] = result.errorsCount || 0;
              row['Difficulty Index'] = result.difficultyIndex || 0;
              row['DDA Action'] = result.ddaAction || 'N/A';
            } else if (session.gameType === 'MemoryPalace') {
              row['Grid Size'] = `${result.gridSize}x${result.gridSize}`;
              row['Objects Count'] = result.objectsCount || 0;
              row['Correct Placements'] = result.correctPlacements || 0;
              row['Total Placements'] = result.totalPlacements || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Memorization Time'] = result.memorizationTimeSeconds || 0;
            } else if (session.gameType === 'ToneMemory') {
              row['Sequence Length'] = result.sequenceLength || 0;
              row['Round'] = result.round || 1;
              row['Reaction Time (ms)'] = result.reactionTimeMs || 'N/A';
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Completed'] = result.isCompleted ? 'Yes' : 'No';
            } else if (session.gameType === 'ImageMatching') {
              row['Grid Size'] = result.gridSize || 0;
              row['Pairs Count'] = result.pairsCount || 0;
              row['Matches Found'] = result.matchesFound || 0;
              row['Moves Count'] = result.movesCount || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Completed'] = result.isCompleted ? 'Yes' : 'No';
            } else if (session.gameType === 'TickerTape') {
              row['Stocks Count'] = result.stocksCount || 0;
              row['Correct Answers'] = result.correctAnswers || 0;
              row['Total Answers'] = result.totalAnswers || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Scroll Speed'] = result.scrollSpeedPixelsPerSecond || 0;
            }

            exportData.push(row);
          });
        }
      });
    });

    // Convert to CSV
    if (exportData.length === 0) {
      alert('No data available in the database to export.');
      return;
    }

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `CognitiveGames_ALL_DATA_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => {
      alert(`✓ All data exported successfully!\n\n${exportData.length} rows exported\nFilename: ${filename}\n\nThe file has been downloaded to your Downloads folder.`);
    }, 500);
  };

  // Export filtered data to Excel
  const exportFilteredData = () => {
    // Prepare data for export
    const exportData: any[] = [];
    
    filteredPatients.forEach(patient => {
      patient.sessions.forEach(session => {
        if (session.results && session.results.length > 0) {
          session.results.forEach((result: any, index: number) => {
            const row: any = {
              // Patient Info
              'Patient Name': patient.displayName,
              'Patient Email': patient.email || 'N/A',
              'Patient ID': patient.uid,
              'Patient Type': patient.isAnonymous ? 'Anonymous' : 'Registered',
              
              // Session Info
              'Session ID': session.sessionId,
              'Game Type': getGameTypeLabel(session.gameType),
              'Session Start': formatDate(session.startTime),
              'Session End': session.endTime ? formatDate(session.endTime) : 'Active',
              'Session Duration': formatDuration(session.startTime, session.endTime),
              
              // Result Info
              'Round/Level': result.level || result.round || result.roundNumber || index + 1,
              'Timestamp': formatDate(result.timestamp || result.savedAt),
              'Accuracy %': Math.round(result.accuracyPercentage || result.accuracy || 0),
              'Score': result.score || 0,
            };

            // Add time data
            let time = result.completionTimeSeconds || result.timeSeconds || result.reactionTimeSeconds || 
                       result.time || result.duration || 0;
            if (!time && result.timestampEnd && result.timestampStart) {
              time = (new Date(result.timestampEnd).getTime() - new Date(result.timestampStart).getTime()) / 1000;
            }
            row['Time (seconds)'] = time > 0 ? time : 'N/A';

            // Add game-specific details
            if (session.gameType === 'NumberSequence') {
              row['Grid Size'] = `${result.gridSize}x${result.gridSize}`;
              row['Obstacles'] = result.obstaclesCount || 0;
              row['Moves'] = result.movesCount || 0;
              row['Errors'] = result.errorsCount || 0;
              row['Difficulty Index'] = result.difficultyIndex || 0;
              row['DDA Action'] = result.ddaAction || 'N/A';
            } else if (session.gameType === 'MemoryPalace') {
              row['Grid Size'] = `${result.gridSize}x${result.gridSize}`;
              row['Objects Count'] = result.objectsCount || 0;
              row['Correct Placements'] = result.correctPlacements || 0;
              row['Total Placements'] = result.totalPlacements || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Memorization Time'] = result.memorizationTimeSeconds || 0;
            } else if (session.gameType === 'ToneMemory') {
              row['Sequence Length'] = result.sequenceLength || 0;
              row['Round'] = result.round || 1;
              row['Reaction Time (ms)'] = result.reactionTimeMs || 'N/A';
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Completed'] = result.isCompleted ? 'Yes' : 'No';
            } else if (session.gameType === 'ImageMatching') {
              row['Grid Size'] = result.gridSize || 0;
              row['Pairs Count'] = result.pairsCount || 0;
              row['Matches Found'] = result.matchesFound || 0;
              row['Moves Count'] = result.movesCount || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Completed'] = result.isCompleted ? 'Yes' : 'No';
            } else if (session.gameType === 'TickerTape') {
              row['Stocks Count'] = result.stocksCount || 0;
              row['Correct Answers'] = result.correctAnswers || 0;
              row['Total Answers'] = result.totalAnswers || 0;
              row['Difficulty Level'] = result.difficultyLevel || 0;
              row['Scroll Speed'] = result.scrollSpeedPixelsPerSecond || 0;
            }

            exportData.push(row);
          });
        }
      });
    });

    // Convert to CSV (Excel will open CSV files)
    if (exportData.length === 0) {
      const gameTypeMsg = selectedGameType === 'all' ? 'any game' : getGameTypeLabel(selectedGameType);
      const dateMsg = dateFilter === 'all' ? 'all time' : dateFilter === 'today' ? 'today' : dateFilter === 'week' ? 'last 7 days' : dateFilter === 'month' ? 'last 30 days' : 'custom date range';
      alert(`No data to export.\n\nCurrent filters:\n- Game: ${gameTypeMsg}\n- Date: ${dateMsg}\n- ${filteredPatients.length} patient(s) found\n\nTip: Try changing filters to "All Games" and "All Time" to export all data.`);
      return;
    }

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const gameFilter = selectedGameType === 'all' ? 'AllGames' : selectedGameType;
    const dateFilterName = dateFilter === 'all' ? 'AllTime' : dateFilter;
    const filename = `CognitiveGames_${gameFilter}_${dateFilterName}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show success message
    setTimeout(() => {
      alert(`✓ Export successful!\n\n${exportData.length} rows exported\nFilename: ${filename}\n\nThe file has been downloaded to your Downloads folder.`);
    }, 500);
  };

  // Date filter function
  const filterByDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    const now = new Date();
    
    switch (dateFilter) {
      case 'today':
        return date.toDateString() === now.toDateString();
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return date >= weekAgo;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return date >= monthAgo;
      case 'custom':
        if (!customStartDate && !customEndDate) return true;
        const start = customStartDate ? new Date(customStartDate) : new Date(0);
        const end = customEndDate ? new Date(customEndDate) : new Date();
        return date >= start && date <= end;
      default:
        return true;
    }
  };

  // Filter and sort patients
  const filteredPatients = patients
    .map(p => ({
      ...p,
      sessions: p.sessions.filter(s => {
        const matchesGameType = selectedGameType === 'all' || s.gameType === selectedGameType;
        const matchesDate = filterByDate(s.startTime);
        return matchesGameType && matchesDate;
      })
    }))
    .filter(p => {
      const matchesSearch = p.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           p.uid.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (p.email && p.email.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch && p.sessions.length > 0;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.displayName.localeCompare(b.displayName);
        case 'date':
          return new Date(b.lastLogin).getTime() - new Date(a.lastLogin).getTime();
        case 'sessions':
          return b.sessions.length - a.sessions.length;
        default:
          return 0;
      }
    });

  // Calculate statistics based on filtered data
  const totalPatients = filteredPatients.length;
  const totalSessions = filteredPatients.reduce((sum, p) => sum + p.sessions.length, 0);
  const activePatients = filteredPatients.filter(p => 
    new Date(p.lastLogin).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
  ).length;

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadPatientsData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="welcome-text">Welcome, {user?.displayName}</p>
          </div>
          <div className="header-actions">
            <button onClick={exportAllData} className="refresh-button" style={{backgroundColor: '#8B5CF6'}}>
              📦 Export ALL Data
            </button>
            <button onClick={exportFilteredData} className="refresh-button" style={{backgroundColor: '#10B981'}}>
              📊 Export Filtered Data
            </button>
            <button onClick={loadPatientsData} className="refresh-button">
              🔄 Refresh
            </button>
            <button onClick={() => setShowTutorial(true)} className="refresh-button" style={{backgroundColor: '#4F46E5'}}>
              🎥 Tutorial
            </button>
            <button onClick={logout} className="logout-button">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-label">Total Patients</div>
            <div className="stat-value">{totalPatients}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎮</div>
          <div className="stat-content">
            <div className="stat-label">Total Sessions</div>
            <div className="stat-value">{totalSessions}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Active (7 days)</div>
            <div className="stat-value">{activePatients}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-label">Avg Sessions/Patient</div>
            <div className="stat-value">
              {totalPatients > 0 ? (totalSessions / totalPatients).toFixed(1) : '0'}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-controls">
          <select
            value={selectedGameType}
            onChange={(e) => setSelectedGameType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Games</option>
            <option value="NumberSequence">Number Sequencing</option>
            <option value="MemoryPalace">Memory Palace</option>
            <option value="ToneMemory">Tone Memory</option>
            <option value="ImageMatching">Image Matching</option>
            <option value="TickerTape">Ticker Tape Rush</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as any)}
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="filter-select"
          >
            <option value="date">Sort by: Last Active</option>
            <option value="name">Sort by: Name</option>
            <option value="sessions">Sort by: Session Count</option>
          </select>
        </div>
        {dateFilter === 'custom' && (
          <div className="custom-date-range">
            <div className="date-input-group">
              <label htmlFor="start-date">From:</label>
              <input
                id="start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-group">
              <label htmlFor="end-date">To:</label>
              <input
                id="end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Patients List */}
      <div className="patients-section">
        <h2 className="section-title">Patients ({filteredPatients.length})</h2>
        {filteredPatients.length === 0 ? (
          <div className="empty-state">
            <p>No patients found matching your criteria.</p>
          </div>
        ) : (
          <div className="patients-list">
            {filteredPatients.map((patient) => (
              <div key={patient.uid} className="patient-card">
                <div
                  className="patient-header"
                  onClick={() => togglePatientExpanded(patient.uid)}
                >
                  <div className="patient-info">
                    <div className="patient-avatar">
                      {patient.isAnonymous ? '👤' : '👨‍⚕️'}
                    </div>
                    <div className="patient-details">
                      <h3 className="patient-name">{patient.displayName}</h3>
                      <div className="patient-meta">
                        {patient.email && <span className="meta-item">📧 {patient.email}</span>}
                        <span className="meta-item">🆔 {patient.uid.substring(0, 8)}...</span>
                        <span className="meta-item">
                          {patient.isAnonymous ? '🔓 Anonymous' : '🔒 Registered'}
                        </span>
                      </div>
                      <div className="patient-stats">
                        <span className="stat-badge">
                          {patient.sessions.length} sessions
                        </span>
                        <span className="stat-badge">
                          Last active: {new Date(patient.lastLogin).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="expand-button">
                    {patient.expanded ? '▼' : '▶'}
                  </button>
                </div>

                {patient.expanded && (
                  <div className="patient-sessions">
                    {(() => {
                      const allResults = patient.sessions.flatMap(s => s.results || []);
                      if (allResults.length > 0) {
                        const moca = calculateMoCAScore(allResults);
                        return (
                          <div className="moca-summary-card">
                            <div className="moca-header">
                              <h4>🧠 MoCA Assessment Summary</h4>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedPatientForMoCA(patient); }}
                                className="moca-button"
                              >
                                Full Report
                              </button>
                            </div>
                            <div className="moca-grid">
                              <div className="moca-card">
                                <div className="moca-value">{moca.totalScore.toFixed(1)}</div>
                                <div className="moca-label">Total Score / 30</div>
                                <div className="moca-percent">{moca.percentage.toFixed(0)}%</div>
                              </div>
                              <div className="moca-card">
                                <div className="moca-value-small">{moca.interpretation}</div>
                                <div className="moca-label">Clinical Status</div>
                              </div>
                              <div className="moca-card">
                                <div className="moca-value">{moca.gamesAnalyzed}</div>
                                <div className="moca-label">Games Analyzed</div>
                              </div>
                              <div className="moca-card">
                                <div className="moca-value">{allResults.length}</div>
                                <div className="moca-label">Total Sessions</div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    <h4 className="sessions-title">Game Sessions ({patient.sessions.length})</h4>
                    {patient.sessions.length === 0 ? (
                      <p className="no-sessions">No sessions recorded yet.</p>
                    ) : (
                      <div className="sessions-table-container">
                        <table className="sessions-table">
                          <thead>
                            <tr>
                              <th>Game Type</th>
                              <th>Start Time</th>
                              <th>Duration</th>
                              <th>Status</th>
                              <th>Session ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {patient.sessions
                              .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                              .map((session) => (
                                <React.Fragment key={session.sessionId}>
                                  <tr className="session-row" onClick={() => toggleSessionResults(patient.uid, session.sessionId, session.gameType)}>
                                    <td>
                                      <span className="game-badge">
                                        {getGameTypeLabel(session.gameType)}
                                      </span>
                                    </td>
                                    <td>{formatDate(session.startTime)}</td>
                                    <td>{formatDuration(session.startTime, session.endTime)}</td>
                                    <td>
                                      <span className={`status-badge ${session.isActive ? 'active' : 'completed'}`}>
                                        {session.isActive ? '🟢 Active' : '⚫ Completed'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="session-actions">
                                        <code className="session-id">{session.sessionId.substring(0, 12)}...</code>
                                        <button className="view-results-btn">
                                          {session.resultsExpanded ? '📊 Hide Results' : '📊 View Results'}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  {session.resultsExpanded && (
                                    <tr className="results-row">
                                      <td colSpan={5}>
                                        <div className="session-results">
                                          <h5 className="results-title">Game Results ({session.results?.length || 0} rounds)</h5>
                                          {!session.results || session.results.length === 0 ? (
                                            <p className="no-results">No results recorded for this session.</p>
                                          ) : (
                                            <div className="results-content">
                                              {/* Statistics Summary */}
                                              <div className="results-stats">
                                                <div className="stat-box">
                                                  <span className="stat-label">Total Rounds</span>
                                                  <span className="stat-number">{session.results.length}</span>
                                                </div>
                                                <div className="stat-box">
                                                  <span className="stat-label">Avg Accuracy</span>
                                                  <span className="stat-number">
                                                    {(() => {
                                                      const avg = session.results.reduce((sum, r) => {
                                                        const acc = r.accuracyPercentage ?? r.accuracy ?? 0;
                                                        return sum + acc;
                                                      }, 0) / (session.results.length || 1);
                                                      return isNaN(avg) ? 'N/A' : `${Math.round(avg)}%`;
                                                    })()}
                                                  </span>
                                                </div>
                                                <div className="stat-box">
                                                  <span className="stat-label">Avg Score</span>
                                                  <span className="stat-number">
                                                    {(() => {
                                                      const avg = session.results.reduce((sum, r) => {
                                                        const score = r.score ?? 0;
                                                        return sum + score;
                                                      }, 0) / (session.results.length || 1);
                                                      return isNaN(avg) ? 'N/A' : Math.round(avg);
                                                    })()}
                                                  </span>
                                                </div>
                                                <div className="stat-box">
                                                  <span className="stat-label">Total Time</span>
                                                  <span className="stat-number">
                                                    {(() => {
                                                      const totalTime = session.results.reduce((sum, r) => {
                                                        // Try multiple field names for time
                                                        let time = r.completionTimeSeconds || r.timeSeconds || r.reactionTimeSeconds || 
                                                                   r.time || r.duration || 0;
                                                        
                                                        // Handle milliseconds (convert to seconds)
                                                        if (!time && r.reactionTimeMs) {
                                                          time = r.reactionTimeMs / 1000;
                                                        }
                                                        
                                                        // If no time field, try to calculate from timestamps
                                                        if (!time && r.timestampEnd && r.timestampStart) {
                                                          time = (new Date(r.timestampEnd).getTime() - new Date(r.timestampStart).getTime()) / 1000;
                                                        }
                                                        
                                                        return sum + (typeof time === 'number' ? time : 0);
                                                      }, 0);
                                                      return totalTime > 0 ? `${Math.round(totalTime)}s` : 'N/A';
                                                    })()}
                                                  </span>
                                                </div>
                                              </div>

                                              {/* Performance Charts */}
                                              <div className="charts-section">
                                                <div className="chart-container">
                                                  <h6 className="chart-title">Accuracy Trend</h6>
                                                  <div className="chart-wrapper">
                                                    <svg className="line-chart" viewBox="0 0 400 120" preserveAspectRatio="none">
                                                      <defs>
                                                        <linearGradient id={`gradient-${session.sessionId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                                          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.4" />
                                                          <stop offset="100%" stopColor="#2563eb" stopOpacity="0.05" />
                                                        </linearGradient>
                                                      </defs>
                                                      {(() => {
                                                        const accuracies = session.results.map(r => r.accuracyPercentage || r.accuracy || 0);
                                                        const maxAccuracy = Math.max(...accuracies, 100);
                                                        const points = accuracies.map((acc, i) => {
                                                          const x = (i / (accuracies.length - 1 || 1)) * 400;
                                                          const y = 120 - (acc / maxAccuracy) * 100;
                                                          return `${x},${y}`;
                                                        }).join(' ');
                                                        const areaPoints = `0,120 ${points} 400,120`;
                                                        return (
                                                          <>
                                                            <polyline
                                                              fill={`url(#gradient-${session.sessionId})`}
                                                              points={areaPoints}
                                                            />
                                                            <polyline
                                                              fill="none"
                                                              stroke="#2563eb"
                                                              strokeWidth="2"
                                                              points={points}
                                                            />
                                                            {accuracies.map((acc, i) => {
                                                              const x = (i / (accuracies.length - 1 || 1)) * 400;
                                                              const y = 120 - (acc / maxAccuracy) * 100;
                                                              return (
                                                                <circle
                                                                  key={i}
                                                                  cx={x}
                                                                  cy={y}
                                                                  r="3"
                                                                  fill="#2563eb"
                                                                  stroke="white"
                                                                  strokeWidth="2"
                                                                />
                                                              );
                                                            })}
                                                          </>
                                                        );
                                                      })()}
                                                    </svg>
                                                    <div className="chart-labels">
                                                      <span>Round 1</span>
                                                      <span>Round {session.results.length}</span>
                                                    </div>
                                                  </div>
                                                </div>

                                                <div className="chart-container">
                                                  <h6 className="chart-title">Score Progress</h6>
                                                  <div className="bar-chart">
                                                    {session.results.map((result, i) => {
                                                      const maxScore = Math.max(...(session.results || []).map(r => r.score || 0));
                                                      const height = maxScore > 0 ? ((result.score || 0) / maxScore) * 100 : 0;
                                                      return (
                                                        <div key={i} className="bar-wrapper">
                                                          <div 
                                                            className="bar" 
                                                            style={{ height: `${height}%` }}
                                                            title={`Round ${i + 1}: ${result.score || 0}`}
                                                          >
                                                            <span className="bar-value">{result.score || 0}</span>
                                                          </div>
                                                          <span className="bar-label">{i + 1}</span>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>

                                                <div className="chart-container">
                                                  <h6 className="chart-title">Time per Round</h6>
                                                  <div className="time-chart">
                                                    {session.results.map((result, i) => {
                                                      // Try multiple field names for time
                                                      let time = result.completionTimeSeconds || result.timeSeconds || result.reactionTimeSeconds || 
                                                                 result.time || result.duration || 0;
                                                      
                                                      // If no time field, try to calculate from timestamps
                                                      if (!time && result.timestampEnd && result.timestampStart) {
                                                        time = (new Date(result.timestampEnd).getTime() - new Date(result.timestampStart).getTime()) / 1000;
                                                      }
                                                      
                                                      const validTimes = (session.results || []).map(r => {
                                                        let t = r.completionTimeSeconds || r.timeSeconds || r.reactionTimeSeconds || r.time || r.duration || 0;
                                                        if (!t && r.timestampEnd && r.timestampStart) {
                                                          t = (new Date(r.timestampEnd).getTime() - new Date(r.timestampStart).getTime()) / 1000;
                                                        }
                                                        return t;
                                                      }).filter(t => t > 0);
                                                      const maxTime = validTimes.length > 0 ? Math.max(...validTimes) : 1;
                                                      const width = time > 0 ? (time / maxTime) * 100 : 0;
                                                      return (
                                                        <div key={i} className="time-bar-row">
                                                          <span className="time-label">R{i + 1}</span>
                                                          <div className="time-bar-bg">
                                                            <div 
                                                              className="time-bar-fill" 
                                                              style={{ width: `${width}%` }}
                                                            >
                                                              <span className="time-value">{time > 0 ? `${Math.round(time)}s` : 'N/A'}</span>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Detailed Results Table */}
                                              <div className="results-table-wrapper">
                                                <table className="results-detail-table">
                                                  <thead>
                                                    <tr>
                                                      <th>Round</th>
                                                      <th>Timestamp</th>
                                                      <th>Accuracy</th>
                                                      <th>Score</th>
                                                      <th>Time</th>
                                                      <th>Details</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {session.results.map((result, index) => (
                                                      <tr key={result.resultId || index}>
                                                        <td>{result.level || result.round || result.roundNumber || index + 1}</td>
                                                        <td>{formatDate(result.timestamp || result.savedAt)}</td>
                                                        <td>
                                                          <span className="accuracy-badge">
                                                            {Math.round(result.accuracyPercentage || result.accuracy || 0)}%
                                                          </span>
                                                        </td>
                                                        <td>{result.score || 0}</td>
                                                        <td>
                                                          {(() => {
                                                            // Try multiple field names for time
                                                            let time = result.completionTimeSeconds || result.timeSeconds || result.reactionTimeSeconds || 
                                                                       result.time || result.duration || 0;
                                                            
                                                            // Handle milliseconds (convert to seconds)
                                                            if (!time && result.reactionTimeMs) {
                                                              time = result.reactionTimeMs / 1000;
                                                            }
                                                            
                                                            // If no time field, try to calculate from timestamps
                                                            if (!time && result.timestampEnd && result.timestampStart) {
                                                              time = (new Date(result.timestampEnd).getTime() - new Date(result.timestampStart).getTime()) / 1000;
                                                            }
                                                            
                                                            return time > 0 ? `${Math.round(time)}s` : 'N/A';
                                                          })()}
                                                        </td>
                                                        <td>
                                                          <div className="result-details">
                                                            {session.gameType === 'NumberSequence' && (
                                                              <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                                                <div>Grid: {result.gridSize}x{result.gridSize} | Level: {result.level || 1}</div>
                                                                <div>Obstacles: {result.obstaclesCount || 0} | Moves: {result.movesCount || 0} | Errors: {result.errorsCount || 0}</div>
                                                                <div>Difficulty: {result.difficultyIndex || 0} | DDA: {result.ddaAction || 'N/A'}</div>
                                                              </div>
                                                            )}
                                                            {session.gameType === 'ImageMatching' && (
                                                              <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                                                <div>Grid Size: {result.gridSize || 0} cards | Pairs: {result.pairsCount || 0}</div>
                                                                <div>Matches: {result.matchesFound || 0} | Moves: {result.movesCount || 0}</div>
                                                                <div>Difficulty: {result.difficultyLevel || 0} | Completed: {result.isCompleted ? 'Yes' : 'No'}</div>
                                                              </div>
                                                            )}
                                                            {session.gameType === 'ToneMemory' && (
                                                              <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                                                <div>Sequence Length: {result.sequenceLength || 0} | Round: {result.round || 1}</div>
                                                                <div>Reaction Time: {result.reactionTimeMs ? `${result.reactionTimeMs}ms` : 'N/A'}</div>
                                                                <div>Difficulty: {result.difficultyLevel || 0} | Completed: {result.isCompleted ? 'Yes' : 'No'}</div>
                                                              </div>
                                                            )}
                                                            {session.gameType === 'TickerTape' && (
                                                              <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                                                <div>Stocks: {result.stocksCount || 0} | Round: {result.round || 1}</div>
                                                                <div>Correct: {result.correctAnswers || 0} / {result.totalAnswers || 0}</div>
                                                                <div>Difficulty: {result.difficultyLevel || 0} | Scroll Speed: {result.scrollSpeedPixelsPerSecond || 0}px/s</div>
                                                              </div>
                                                            )}
                                                            {session.gameType === 'MemoryPalace' && (
                                                              <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                                                <div>Grid: {result.gridSize}x{result.gridSize} | Objects: {result.objectsCount || 0}</div>
                                                                <div>Correct: {result.correctPlacements || 0} / {result.totalPlacements || 0}</div>
                                                                <div>Difficulty: {result.difficultyLevel || 0} | Memorization: {result.memorizationTimeSeconds || 0}s</div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {showTutorial && <TutorialVideo onClose={() => setShowTutorial(false)} />}
      
      {selectedPatientForMoCA && (
        <MoCAAssessment
          patientName={selectedPatientForMoCA.displayName}
          results={selectedPatientForMoCA.sessions.flatMap(s => s.results || [])}
          onClose={() => setSelectedPatientForMoCA(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
